import { type JSX, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { createBroadcast, deleteBroadcast, listBroadcasts, type PortalBroadcast } from '../../api.js';
import { confirmDialog } from '../../components/dialogs.js';

type Severity = 'info' | 'warning' | 'critical';
const SEV_COLOR: Record<Severity, 'info' | 'warning' | 'error'> = { info: 'info', warning: 'warning', critical: 'error' };

/** datetime-local string → unix seconds (or undefined when blank). */
function toUnix(input: string): number | undefined {
  if (input.trim() === '') return undefined;
  const ms = new Date(input).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}
function defaultExpiry(): string {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Post short messages to a fleet's launcher bell panel; manage the list. */
export function BroadcastsSection({ tenantId, editToken }: { tenantId: string; editToken: string }): JSX.Element {
  const [rows, setRows] = useState<PortalBroadcast[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<Severity>('info');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);
  const [dismissible, setDismissible] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function load(): Promise<void> {
    const res = await listBroadcasts({ tenantId, editToken });
    if (res.kind === 'ok') setRows(res.broadcasts);
    else setLoadError(res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.`);
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const expUnix = toUnix(expiresAt);
  const startUnix = toUnix(startsAt);
  const valid =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    expUnix !== undefined &&
    (startUnix === undefined || expUnix > startUnix);

  async function create(): Promise<void> {
    if (!valid || expUnix === undefined) return;
    setBusy(true);
    setMsg(null);
    const res = await createBroadcast({
      tenantId,
      editToken,
      title: title.trim(),
      body: body.trim(),
      severity,
      expiresAt: expUnix,
      dismissible,
      ...(startUnix !== undefined ? { startsAt: startUnix } : {}),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      setMsg({ kind: 'ok', text: `Broadcast “${title.trim()}” posted.` });
      setTitle('');
      setBody('');
      setSeverity('info');
      setStartsAt('');
      setExpiresAt(defaultExpiry());
      void load();
    } else {
      setMsg({ kind: 'err', text: res.kind === 'invalid' ? res.message : res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.` });
    }
  }

  async function remove(b: PortalBroadcast): Promise<void> {
    const ok = await confirmDialog({
      title: 'Delete broadcast',
      message: `Delete “${b.title}”? It stops showing on devices at their next poll.`,
      destructive: true,
      confirmLabel: 'Delete',
      requireText: 'delete',
    });
    if (!ok) return;
    const res = await deleteBroadcast({ tenantId, editToken, id: b.id });
    if (res.kind === 'ok' || res.kind === 'not-found') {
      setMsg({ kind: 'ok', text: 'Broadcast deleted.' });
      void load();
    } else {
      setMsg({ kind: 'err', text: res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.` });
    }
  }

  const now = Math.floor(Date.now() / 1000);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700}>Broadcasts</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Short messages devices show in the launcher’s bell panel. Active broadcasts appear within a minute.
      </Typography>

      {msg !== null && <Alert severity={msg.kind === 'ok' ? 'success' : 'error'} sx={{ mb: 2 }}>{msg.text}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 80, 'data-testid': 'broadcast-title' }} fullWidth />
            <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} inputProps={{ maxLength: 500 }} multiline minRows={2} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value as Severity)} sx={{ minWidth: 140 }}>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </TextField>
              <TextField label="Starts (optional)" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Expires" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} InputLabelProps={{ shrink: true }} />
              <FormControlLabel control={<Switch checked={dismissible} onChange={(e) => setDismissible(e.target.checked)} />} label="Dismissible" />
            </Stack>
          </Stack>
        </CardContent>
        <Box sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" onClick={() => void create()} disabled={busy || !valid} data-testid="broadcast-post">
            {busy ? 'Posting…' : 'Post broadcast'}
          </Button>
        </Box>
      </Card>

      {loadError !== null ? (
        <Alert severity="error">{loadError}</Alert>
      ) : rows === null ? (
        <Typography color="text.secondary">Loading broadcasts…</Typography>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No broadcasts yet.</Typography>
      ) : (
        <Stack spacing={1.5} data-testid="broadcast-list">
          {rows.map((b) => {
            const state = b.startsAt > now ? 'scheduled' : b.expiresAt <= now ? 'expired' : 'active';
            return (
              <Card key={b.id} variant="outlined">
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Chip size="small" label={b.severity} color={SEV_COLOR[b.severity as Severity]} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{b.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {state} · expires {new Date(b.expiresAt * 1000).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{b.body}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => void remove(b)} aria-label={`Delete ${b.title}`}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
